/** Data hooks for the ledger: the filtered, paginated transaction list. */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { api, type Member, type TransactionPage } from "../../lib/api";
import { serializeFilters, type LedgerFilters } from "../../lib/filters";

export const PAGE_SIZE = 100;

export function ledgerQueryKey(groupId: number, filters: LedgerFilters) {
  return ["transactions", groupId, serializeFilters(filters).toString()] as const;
}

export function useLedger(groupId: number, filters: LedgerFilters) {
  return useInfiniteQuery({
    queryKey: ledgerQueryKey(groupId, filters),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) => {
      const params = serializeFilters(filters);
      params.set("limit", String(PAGE_SIZE));
      if (pageParam) params.set("cursor", pageParam);
      return api.get<TransactionPage>(`/groups/${groupId}/transactions?${params}`, signal);
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: Number.isFinite(groupId),
  });
}

export function useMembers(groupId: number) {
  return useQuery({
    queryKey: ["members", groupId],
    queryFn: () => api.get<Member[]>(`/groups/${groupId}/members`),
    enabled: Number.isFinite(groupId),
    staleTime: 60_000,
  });
}

export function useCategories(groupId: number) {
  return useQuery({
    queryKey: ["categories", groupId],
    queryFn: () =>
      api.get<{ id: number; name: string; is_fallback: boolean }[]>(
        `/groups/${groupId}/categories`,
      ),
    enabled: Number.isFinite(groupId),
    staleTime: 60_000,
    retry: false,
  });
}

/** `{member_id: display_name}` for the columns and chips. */
export function memberNames(members: Member[] | undefined): Map<number, string> {
  return new Map((members ?? []).map((member) => [member.id, member.display_name]));
}
